/**
 * VoiceEngine — the portable voice-conversation brain: state machine (idle|listening|thinking|
 * speaking), barge-in policy (AEC-trivial + heuristic echo tier), gapless TTS turn handoff,
 * utterance merge window, interrupted-reply tracking, ack echo-leak guard.
 *
 * Pure logic over three injected seams (STT, TTS, AudioSink) — runs in CLI, server, or browser.
 * Hosts wire `onUtterance` to their dispatch and feed `beginSpeech()/speakDelta()/endSpeech()`;
 * `interrupt()` is the barge-in primitive. Behavior validated live; see mind/11-voice.md.
 */
import { forComponent } from '../logging';
import { EmotionStream } from './emotion';
import type { AudioSink, VoiceState } from './types';

const log = forComponent('VoiceEngine');

/** Injectable time source — ALL engine timing (merge windows, barge grace, overlap resume, drain
 *  settles) routes through it. Defaults to real timers; the deterministic bench swaps a virtual
 *  clock so timing scenarios run instantly and exactly. */
export interface EngineClock {
  now(): number;
  setTimeout(fn: () => void, ms: number): unknown;
  clearTimeout(handle: unknown): void;
}
export const realClock: EngineClock = {
  now: () => performance.now(),
  setTimeout: (fn, ms) => setTimeout(fn, ms),
  clearTimeout: (h) => clearTimeout(h as ReturnType<typeof setTimeout>),
};

/** One structured diagnostic event (fire-and-forget, never affects behavior). `t` = engine clock ms.
 *  Emitted at every DECISION point; kinds + fields are the per-session forensic record
 *  (CLI: `<session>.voice.jsonl`; lab: `<session>.timeline.jsonl`). See mind/11-voice.md §Diagnostics. */
export interface DiagEvent { t: number; kind: string; [k: string]: unknown }

/** Sanitize text for the spoken channel (and its on-screen echo). Drops markdown punctuation TTS would
 *  read aloud ("star"/"hash"), turns em/en dashes and table pipes into spoken pauses, normalizes "30 %"
 *  → "30%", and collapses filler dot-runs ("now....." → "now."). Per-delta safe: every map is local to
 *  the chunk (single chars / line-anchored), so chunk boundaries never split a multi-char span. */
export const forSpeech = (t: string): string =>
  t.replace(/[*`#]+/g, '')
   // underscores: strip only EMPHASIS pairs (_word_ at word boundaries) — intra-word underscores are
   // real content (EXPORT_PLAYBOOK.txt, .DS_Store) and must survive in both speech and echo
   .replace(/(?<![\p{L}\p{N}])_([^_\n]+)_(?![\p{L}\p{N}])/gu, '$1')
   .replace(/^[ \t]*[-•]\s+/gm, '')        // leading list marker
   .replace(/\s*[\u2013\u2014]\s*/g, ', ')  // en/em dash → spoken pause (prompt forbids dashes in speech)
   .replace(/[\u2010\u2011]/g, '-')          // (non-breaking) hyphen → plain hyphen (TTS reads "8-12" fine)
   .replace(/\s*\|\s*/g, ', ')             // table pipe → pause
   .replace(/(\d)\s+%/g, '$1%')       // "30 %" → "30%" (else "thirty space percent")
   .replace(/\.{3,}/g, '.');          // filler dot-run → single period

const normWord = (w: string): string => w.toLowerCase().replace(/[^a-z0-9]/g, '');

/** Trailing extra words the final may add WITHOUT changing what the reflex must answer — politeness/
 *  discourse fillers only. Any CONTENT word ("…very short" + "joke") means the speculation answered a
 *  truncated thought and must abort (live-confirmed quality bug: prosodic-pause partials). */
const SPEC_TRAILING_OK = new Set(['please', 'thanks', 'thank', 'you', 'now', 'okay', 'ok', 'kindly', 'alright', 'then', 'though', 'right', 'yeah']);

/** Does the endpointed FINAL confirm a SPECULATED partial? Exact normalized match, or the speculation
 *  is a word-prefix of the final with ≤2 trailing extra NON-CONTENT words (SPEC_TRAILING_OK — the
 *  speculated words must all land; the last one may be a char-prefix of its final counterpart, a
 *  mid-word partial). The same prefix shape as refinalizes(), applied host-side to decide
 *  confirm-vs-abort of a speculative reflex call. */
export function speculationConfirms(spec: string, final: string): boolean {
  const sw = spec.trim().split(/\s+/).map(normWord).filter(Boolean);
  const fw = final.trim().split(/\s+/).map(normWord).filter(Boolean);
  if (!sw.length || fw.length < sw.length || fw.length > sw.length + 2) return false;
  for (let i = 0; i < sw.length - 1; i++) if (sw[i] !== fw[i]) return false;
  if (!fw[sw.length - 1].startsWith(sw[sw.length - 1])) return false;
  for (let i = sw.length; i < fw.length; i++) if (!SPEC_TRAILING_OK.has(fw[i])) return false;
  return true;
}

/** Structural contracts (satisfied by SonioxSTT/CartesiaTTS or test fakes). */
export interface SttLike {
  usingAec: boolean;
  onPartial: (text: string) => void;
  onUtterance: (text: string, endpointAt: number) => void;
  onLevel: (rms: number) => void;
  /** Optional: unrecoverable capture failure (e.g. mic produced no audio) — host tears voice down. */
  onFatal?: (message: string) => void;
  start(): Promise<void> | void;
  reset(): void;
  stop(): void;
}
export interface TtsLike {
  onAudio: (chunk: Uint8Array) => void;
  onDone: () => void;
  /** Optional word-timestamp seam for karaoke reveal (revealMode==='word'). `start[i]` = seconds
   *  from turn-audio start. Set `wantTimestamps` to request them from the provider. */
  onTimestamps?: (words: string[], start: number[]) => void;
  wantTimestamps?: boolean;
  connect(): Promise<void> | void;
  /** Optional: prime the synthesis pipeline right after connect (throwaway context, audio discarded)
   *  so the FIRST real turn doesn't pay the provider's cold-synthesis spin-up. */
  warmup?(): void;
  newContext(): string;
  speak(text: string, cont: boolean): void;
  end(): void;
  cancel(): void;
  close(): void;
}

export class VoiceEngineOptions {
  stt!: SttLike;
  tts!: TtsLike;
  player!: AudioSink;
  /** a final utterance arrived (endpoint) — host dispatches it as a turn */
  onUtterance: (text: string) => void = () => {};
  /** live partial transcript while listening (host renders the 🎤 line) */
  onPartial: (text: string) => void = () => {};
  onState: (s: VoiceState) => void = () => {};
  /** user spoke/acted over playback — host aborts the in-flight turn (called AFTER audio is killed).
   *  phase: 'speaking' = cut mid-speech (real interruption); 'drain' = in the final audio tail
   *  (normal turn-taking — hosts shouldn't alarm). */
  onBargeIn: (phase: 'speaking' | 'drain') => void = () => {};
  /** spoken micro-ack on utterance endpoint (masks LLM TTFT); '' disables */
  ackPhrase = '';
  /** ADAPTIVE micro-ack: on an utterance-dispatched turn, speak a short varied ack ONLY if no reflex
   *  delta has arrived after this many ms (masks a slow TTFT without acking every turn — a fixed
   *  per-turn ack was rejected as robotic). First delta / interrupt / hold cancels it. 0 = off. */
  adaptiveAckMs = 0;
  /** the adaptive ack actually fired (host can mark the turn as spoken — e.g. suppress dead-air repair) */
  onAdaptiveAck: () => void = () => {};
  /** Endpoint merge window (ms): hold an endpointed utterance briefly — if speech resumes (spelled
   *  letters, mid-thought pauses), the next utterance MERGES instead of dispatching a truncated one
   *  ("E-L-Y." / "A."). Costs this much latency per turn; 0 disables. */
  utteranceMergeMs = 350;
  /** Extended merge window (ms) for utterances that look incomplete (trailing conjunction/filler).
   *  Gives the user time to finish their thought without triggering a model call. */
  incompleteMergeMs = 1500;
  /** Grace window (ms) after an utterance dispatches, during which the user's own trailing audio cannot
   *  barge the reply it requested. Soniox keeps finalizing partials past <end>; without this they read
   *  as a barge and abort the fresh turn (live: mid-sentence self-interruption + steps=1→steps=0 double
   *  abort). Short enough that a genuine immediate barge ("no wait—") still lands right after. */
  bargeGraceMs = 600;
  /** Barge-in (talk over the assistant to interrupt). true = full-duplex (needs echo cancellation, or
   *  the assistant's own TTS bleeds back and self-interrupts). false = HALF-DUPLEX: the engine is deaf
   *  while audible (speaking + drain tail), so echo can never become a phantom turn — the right mode
   *  when there's no AEC (e.g. the non-VPIO mic fallback) and no headphones. Cost: can't interrupt. */
  bargeIn = true;
  /** Filler phrase spoken when holding for an incomplete utterance ('' disables). */
  holdFiller = '';
  /** Called when the engine holds an incomplete utterance (host can render a visual cue). */
  onHold: () => void = () => {};
  /** heuristic (non-AEC) energy barge-in tuning */
  bargeRmsMult = 2;
  bargeRmsFloor = 500;
  /** Overlap turn-taking (AEC tier, needs player.pause/resume) — human phone-call model, driven by
   *  the STT ITSELF (a trained speech classifier) instead of energy thresholds (energy could not
   *  separate residue bursts from speech in every room — hiccup whack-a-mole): a GENUINE partial
   *  (novel words dominate — echo of our own reply is inert) while speaking → PAUSE (exact-sample
   *  hold); partial grows into dominant-novel ≥2 words → cede (interrupt; the LLM re-enters); partial
   *  stalls/endpoints without ceding (backchannel by DURATION, not vocabulary) → resume + drop. false disables. */
  overlapPause = true;
  /** no new partial activity for this long while paused → resume, drop the interjection */
  overlapResumeMs = 700;
  /** A genuine barge over a LONG reply is defeated by the dominant-novel gate: Meet echoes our own
   *  speech back, so the partial is mostly our words + a few of hers → never "dominant novel" → it
   *  resumes (replaying old audio — the audible "completes the buffer" blip) instead of ceding.
   *  Mechanism-based discriminator: a re-PAUSE this soon after a resume = a persistent human, not an
   *  echo blip (which pauses once and stalls). Cede on the re-pause regardless of the novel gate. */
  overlapRepauseCedeMs = 1500;
  /** Speculative ENERGY pre-pause while speaking (AEC tier): two residue gate-passes within 350ms →
   *  pause ~300ms before the STT tokens land. But energy CANNOT separate residue bursts from speech
   *  (the documented whack-a-mole) — so a residue spike during loud playback false-pauses with NO user
   *  speech at all, an audible hiccup. Default OFF: the genuine-gated STT partial is the
   *  mechanism-correct pause trigger; enable only if barge-in onset feels sluggish in a clean-AEC room. */
  overlapEnergyHold = false;
  /** SPECULATIVE REFLEX START (the root TTFT fix): a partial transcript that has stopped changing for
   *  this many ms AND carries ≥ speculativeMinWords is "stable" — `onSpeculate` fires so the host can
   *  start the reflex EARLY, ~endpoint+merge (500-850ms) before the final would dispatch. The
   *  speculative call's output is HELD by the host (nothing reaches TTS) until the endpointed final
   *  confirms it (see speculationConfirms). At most one speculation per turn-in-progress. 0 = off. */
  speculativeMs = 0;
  /** Minimum word count for a partial to qualify as a speculation trigger. */
  speculativeMinWords = 4;
  /** A stable partial (speculativeMs) — the host starts a HELD speculative reflex call. */
  onSpeculate: (text: string) => void = () => {};
  /** AGENT-SIDE BACKCHANNELING (rule-based v1): while LISTENING to a long multi-clause user turn, a
   *  partial that reaches a clause boundary (trailing [,.;!?] or conjunction/filler) and then stays
   *  UNCHANGED for this many ms (a micro-pause — before the silence endpoint fires) triggers a short
   *  quiet TTS blip ("Mm-hm.") on a throwaway context. ZERO floor-claim: no state change, no timers
   *  touched, no turn context — audio passes a narrow gate bypass and a real turn supersedes it via
   *  context rotation. Latin-predominant partials only (Hebrew/mixed text never misfires — the
   *  boundary/conjunction heuristics are English-tuned, so non-Latin turns simply get no blips).
   *  0 = off (default). ~200-300 recommended: live, Soniox's SEMANTIC endpoint (<end>) lands within
   *  ~300-400ms of a clause pause — a longer stability window loses the race and never fires. */
  backchannelMs = 0;
  /** Min gap between blips (rate limit); additionally max 2 blips per user turn-in-progress. */
  backchannelMinGapMs = 8000;
  /** Only multi-clause turns: the partial must carry at least this many words before a blip. */
  backchannelMinWords = 8;
  /** A backchannel blip was spoken (host renders a timeline event; the blip is NOT a reply). */
  onBackchannel: (phrase: string) => void = () => {};
  /** The partial outgrew the speculated text (user kept talking) — the host aborts the speculation
   *  quietly (the endpointed final will also refuse to confirm; this just stops the billing earlier). */
  onSpeculateAbort: () => void = () => {};
  /** Map inline `[emotion]` tags (emitted by the model, prompt-taught) into Cartesia inline emotion
   *  tags in the spoken transcript (sonic-3 stitches the prosody). false = strip them silently. */
  emotions = true;
  /** Show the `[emotion]` tags in the on-screen echo (debug). false = hide (spoken-only). */
  showEmotions = false;
  /**
   * Progressive text reveal — the "karaoke" capability, opt-in.
   *   'off'   — no reveal events (CLI default; the host renders text however it likes).
   *   'delta' — emit `onReveal(fullText)` as each spoken delta lands (near-free; text grows
   *             in step with the model stream).
   *   'word'  — reveal word-by-word synced to the TTS playback CLOCK (true karaoke). Requires a
   *             timestamp-emitting TTS (CartesiaTTS.wantTimestamps) driven over `AudioSink.playedMs`.
   */
  revealMode: 'off' | 'delta' | 'word' = 'off';
  /** Called with the cumulative revealed text for the current assistant turn (see `revealMode`).
   *  Reset to '' at the start of each spoken turn. */
  onReveal: (revealed: string) => void = () => {};
  /** Time source for every engine timer/timestamp. Default: real time (identical behavior). */
  clock: EngineClock = realClock;
  /** Structured diagnostic event at every decision point (state transitions, dispatch/drop paths,
   *  barge-in, overlap pause/resume, acks, speculation, backchannels, echo swallows). Fire-and-forget:
   *  a throwing handler is caught once and diagnostics disable — engine behavior is never affected. */
  onDiag: (ev: DiagEvent) => void = () => {};
}

export class VoiceEngine {
  public options: VoiceEngineOptions;
  public state: VoiceState = 'idle';
  protected stt: SttLike;
  protected tts: TtsLike;
  protected player: AudioSink;
  private speaking = false; // audible (deltas flowing OR audio draining)
  private ctxOpen = false; // the current TTS context still accepts deltas (false once end-frame sent)
  private interrupted = false; // barge-in latch: drop in-flight deltas until the next legitimate turn
  private spokeDeltas = false; // a TTS context is open for the current spoken turn
  private revealText = ''; // cumulative revealed (on-screen) text for the current turn — see revealMode
  // 'word' karaoke reveal: spoken-word start times (sec, absolute from turn-audio start) + poll state.
  private wordStarts: number[] = [];
  private revealedN = 0;
  private revealPoll: unknown = null;
  private clock: EngineClock;
  private drainTimer: unknown = null;
  // heuristic tier state (inert under AEC) — frozen as validated in the experiment
  private echoWords = new Set<string>();
  private prevReply = '';
  private reply = '';
  private echoUntil = 0;
  private baseline = 0;
  private hot = 0;
  private suspectUntil = 0;
  private ackAt = 0; // when the micro-ack was spoken — its echo can leak before the AEC filter converges
  private lastAck = ''; // the exact ack text last spoken (fixed OR adaptive) — the echo-leak guard matches it
  private ackTimer: unknown = null; // one-shot adaptive-ack timer (armed at dispatch, cancelled on first delta)
    private bargeGraceUntil = 0; // no barge-in until this time — the user's OWN trailing audio (after the
  // utterance that JUST dispatched this turn) must not immediately re-interrupt the reply it requested.
  private pendingUtt = ''; // endpointed text held for the merge window
  private mergePath: 'direct' | 'merged' = 'direct'; // how the pending utterance was assembled (diag)
  private lastGraceDiag = 0; // grace_suppress emitted once per grace window
  private pendingTimer: unknown = null;
  // Duplicate-final guard: STT sometimes re-finalizes the SAME audio a beat later (past the merge
  // window) — the identical utterance dispatched twice with no reply in between (live: "When you
  // start" twice). Conservative: only an IDENTICAL (normalized) text, within dupFinalMs, with zero
  // reply deltas since the first dispatch, is dropped. A user genuinely repeating themselves after
  // the agent replied (or after 3s) still dispatches.
  private lastDispatchFlat = '';
  private lastDispatchWords: string[] = [];
  private lastDispatchAt = 0;
  private repliedSinceDispatch = false;
  private static readonly DUP_FINAL_MS = 3000;
  private lastInterrupted: { full: string; heard: string } | null = null;
  // overlap (pause) tier state — AEC + pause-capable sinks only
  private pausedAt = 0;
  private lastResumeAt = 0; // when the overlap last resumed from a false alarm — a quick re-pause cedes
  private lastOverlapPartial = ''; // change-detection: only NEW partial text counts as activity
  private resumeTimer: unknown = null;
  private turnStartAt = 0; // timestamp when the current turn began (for TTFT logging)
  // speculative reflex trigger state (options.speculativeMs) — see trackSpeculation
  private specPartial = ''; // last partial observed (stability = unchanged for speculativeMs)
  private specTimer: unknown = null;
  private specText = '';    // text handed to onSpeculate ('' = no speculation in flight)
  private specSpent = false; // at most one speculation per turn-in-progress
  // backchannel blip state (options.backchannelMs) — see trackBackchannel
  private bcPartial = '';      // last partial observed (stability = unchanged for backchannelMs)
  private bcTimer: unknown = null;
  private bcCount = 0;         // blips this user turn-in-progress (max 2; reset at dispatch)
  private bcActive = false;    // NARROW audio-gate bypass: blip audio may reach the sink while listening
  private bcActiveTimer: unknown = null; // safety: clear the bypass even if the blip's 'done' is lost
  private lastBcAt = 0;
  private lastBcPhrase = '';   // exact blip text last spoken — flushUtterance strips its mic echo
  private recentBc: string[] = [];
  // Central speech queue (above the TTS context): complete worker utterances serialize into ONE
  // playback stream, one-at-a-time, never splicing into the live reflex's open utterance.
  private uttQueue: string[] = [];
  // Per-turn emotion-tag parser (reset on beginSpeech) — converts `[emotion]` → Cartesia inline tags
  // for TTS, tracks tag-free prose for echo discrimination, and surfaces display text for the screen.
  private emo: EmotionStream | null = null;

  constructor(options?: Partial<VoiceEngineOptions>) {
    this.options = { ...new VoiceEngineOptions(), ...options };
    const o = this.options;
    if (!o.stt || !o.tts || !o.player) throw new Error('VoiceEngine needs stt, tts and player (see cli/voice.ts VoiceIO for platform defaults)');
    this.stt = o.stt;
    this.tts = o.tts;
    this.player = o.player;
    this.clock = o.clock;
  }

  async start(): Promise<void> {
    // `speaking` gates real-turn audio; `bcActive` is the ONLY other pass — a backchannel blip's
    // throwaway context, allowed through while listening (unlike warmup, whose audio this gate drops).
    this.tts.onAudio = (c) => { if (this.speaking || this.bcActive) this.player.write(c); };
    // Karaoke reveal: ask the TTS for word timestamps and accumulate them for the playback-synced poll.
    if (this.options.revealMode === 'word') {
      this.tts.wantTimestamps = true;
      this.tts.onTimestamps = (_words, start) => {
        if (this.interrupted) return;
        for (const s of start) this.wordStarts.push(s);
      };
    }
    this.stt.onPartial = (text) => this.handlePartial(text);
    this.stt.onUtterance = (text) => this.handleUtterance(text);
    this.stt.onLevel = (rms) => this.handleLevel(rms);
    await Promise.all([this.tts.connect(), this.stt.start()]);
    // Pre-warm at mic-on: prime the TTS synthesis pipeline (throwaway context; any audio it returns
    // is dropped by the `speaking` gate above / stale-context filter) so the first real turn doesn't
    // pay the provider's cold-synthesis spin-up (~1s+ live on Cartesia).
    this.tts.warmup?.();
    this.setState('listening');
    log.debug(`voice I/O up (${this.stt.usingAec ? 'AEC' : 'heuristic echo'} capture)`);
  }

  get usingAec(): boolean {
    return this.stt.usingAec;
  }

  /** Flip barge-in at runtime (e.g. the mic fell back to non-VPIO → go half-duplex so echo can't leak). */
  setBargeIn(on: boolean): void { this.options.bargeIn = on; }

  /** Show/hide the `[emotion]` debug tags in the echo (next turn's stream picks it up). */
  setShowEmotions(on: boolean): void { this.options.showEmotions = on; }

  /** Diagnostics tap (options.onDiag). Fire-and-forget: a throwing handler disables the tap once —
   *  it can NEVER perturb engine behavior. Protected so VoiceIO can route provider events through it. */
  private diagOn = true;
  protected diag(kind: string, fields?: Record<string, unknown>): void {
    if (!this.diagOn) return;
    try { this.options.onDiag({ t: this.clock.now(), kind, ...fields }); }
    catch (e) { this.diagOn = false; log.debug(`onDiag threw — diagnostics disabled: ${e instanceof Error ? e.message : e}`); }
  }

  private idleWaiters: (() => void)[] = [];
  private setState(s: VoiceState): void {
    if (this.state === s) return;
    this.diag('state', { from: this.state, to: s });
    this.state = s;
    this.options.onState(s);
    if (s !== 'speaking' && s !== 'thinking') {
      for (const r of this.idleWaiters.splice(0)) r();
    }
  }

  /** Resolve when the engine is no longer speaking (immediate if already idle). */
  awaitIdle(): Promise<void> {
    if (this.state !== 'speaking' && this.state !== 'thinking') return Promise.resolve();
    return new Promise((r) => this.idleWaiters.push(r));
  }

  // --- speaking side (host-driven) ---

  /** open a spoken turn (idempotent — safe from both onUtterance and first-delta paths).
   *  `ack` speaks the configured micro-ack as the context opener (utterance path only —
   *  masks LLM TTFT; re-voice turns begun by their first delta skip it). */
  beginSpeech(ack = false): void {
    if (this.speaking && this.ctxOpen) return;
    if (this.drainTimer) { this.clock.clearTimeout(this.drainTimer); this.drainTimer = null; }
    this.interrupted = false; // a new turn re-arms speech
    this.bcSupersede(); // a real turn mid-blip: close the audio bypass; newContext() below rotates the
    // ctx so Cartesia's stale-context filter drops any blip stragglers, and markTurn cuts the tail.
    // gapless handoff: a new turn while the previous one's audio drains keeps the SAME player
    // (markTurn would cut the tail) and just opens a fresh TTS context — Cartesia rejects deltas
    // on an ended context ("Context closed" 400, narration silently lost).
    this.resetOverlap(true); // a held pause must release — gapless handoff keeps the same player (no FLUSH)
    this.diag('tts_turn_begin', { ack, gapless: this.speaking });
    if (!this.speaking) this.player.markTurn();
    this.speaking = true;
    this.ctxOpen = true;
    this.spokeDeltas = false;
    this.reply = '';
    this.revealText = '';
    this.wordStarts = [];
    this.revealedN = 0;
    if (this.options.revealMode === 'word') this.startWordReveal();
    this.emo = this.options.emotions ? new EmotionStream(this.options.showEmotions) : null;
    this.echoWords = new Set(this.words(this.prevReply)); // previous reply's echo may still be in flight
    this.tts.newContext();
    this.clearAckTimer('new_turn'); // a fresh turn supersedes any armed ack from a previous one
    if (ack && this.options.ackPhrase) {
      this.diag('ack_fired', { phrase: this.options.ackPhrase, adaptive: false });
      this.tts.speak(this.options.ackPhrase + ' ', true);
      this.spokeDeltas = true; this.ackAt = this.clock.now(); this.lastAck = this.options.ackPhrase;
      for (const w of this.words(this.options.ackPhrase)) this.echoWords.add(w); // ack is OUR audio — its echo must not read as novel speech
    }
    // Adaptive micro-ack: arm a one-shot timer — a short varied ack speaks ONLY if the reflex is still
    // silent when it fires (slow TTFT); the reply then continues the same TTS context. First delta
    // cancels it (fast turns stay ack-free); interrupt/endSpeech/hold cancel too.
    else if (ack && this.options.adaptiveAckMs > 0) {
      this.ackTimer = this.clock.setTimeout(() => {
        this.ackTimer = null;
        if (!this.speaking || !this.ctxOpen || this.spokeDeltas || this.interrupted) return;
        const phrase = this.pickAck();
        this.diag('ack_fired', { phrase, adaptive: true, waitedMs: this.options.adaptiveAckMs });
        this.tts.speak(phrase + ' ', true);
        this.spokeDeltas = true;
        this.ackAt = this.clock.now();
        this.lastAck = phrase;
        for (const w of this.words(phrase)) this.echoWords.add(w); // ack is OUR audio — its echo must not read as novel speech
        this.setState('speaking'); // ack audio is playing — don't sit in "thinking" while audibly speaking
        this.options.onAdaptiveAck();
      }, this.options.adaptiveAckMs);
    }
    // TTFT is stamped at the real turn start (flushUtterance, when the user's utterance dispatches);
    // only fall back to here for a re-voice turn that has no preceding utterance.
    if (!this.turnStartAt) this.turnStartAt = this.clock.now();
    this.setState('thinking');
  }
  /** Feed a spoken delta. Returns the on-screen echo text (emotion tags shown/hidden per config) so the
   *  host renders the SAME stream that was parsed for TTS — no second, state-doubling parse. */
  speakDelta(text: string): string {
    // After a barge-in the aborted turn's stream may keep delivering (re-voice turns have no abort
    // signal) — those deltas must NOT re-open speech. endSpeech()/beginSpeech() clear the latch.
    if (this.interrupted) return '';
    this.clearAckTimer('first_delta'); // the reflex spoke in time — no ack needed
    if (!this.speaking || !this.ctxOpen) this.beginSpeech();
    // Split the delta into spoken transcript (Cartesia emotion tags inline), tag-free prose (echo
    // discrimination), and screen-echo display. The stream buffers a tag split across chunk boundaries.
    let { speech, display, prose } = this.emo ? this.emo.feed(text) : { speech: text, display: text, prose: text };
    // Boundary-space repair: some models emit consecutive sentence deltas with no separating space
    // ("…Israel." + "Anything else?") — the join renders AND speaks as "Israel.Anything". If the
    // forwarded prose so far ends in sentence punctuation and this delta starts immediately with an
    // uppercase letter, insert the missing space. Uppercase-only on purpose: "3." + "5" (a split
    // decimal) must NOT gain a space.
    if (this.reply && /[.!?…]$/.test(this.reply) && /^[A-Z]/.test(prose)) { speech = ' ' + speech; display = ' ' + display; prose = ' ' + prose; }
    this.reply += prose;
    if (prose.trim()) this.repliedSinceDispatch = true; // an agent reply arrived — a later identical final is a genuine repeat
    for (const w of this.words(this.reply)) this.echoWords.add(w);
    this.tts.speak(forSpeech(speech), true); // strip markdown punctuation so TTS doesn't read "dash"/"star"/"hash" aloud
    if (!this.spokeDeltas && this.turnStartAt) log.debug(`ttft: ${Math.round(this.clock.now() - this.turnStartAt)}ms`);
    this.spokeDeltas = true;
    this.setState('speaking');
    // Progressive reveal (opt-in). 'delta' fires per-delta with the growing on-screen text; 'word'
    // accumulates the text here but reveals via the playback-synced poll (startWordReveal).
    if (this.options.revealMode !== 'off' && display) {
      this.revealText += display;
      if (this.options.revealMode === 'delta') this.options.onReveal(this.revealText);
    }
    return display;
  }
  /** Karaoke reveal poll: reveal the display-word prefix up to the count of spoken words whose
   *  Cartesia start time has been reached on the playback clock (`AudioSink.playedMs`, which excludes
   *  paused time). Poll-driven (not AudioContext timers) so it's portable across CLI/browser sinks and
   *  deterministic under the virtual clock. */
  private startWordReveal(): void {
    if (this.revealPoll) return;
    const tick = () => {
      this.revealPoll = null;
      if (!this.speaking || this.interrupted || this.options.revealMode !== 'word') return;
      const playedSec = this.player.playedMs() / 1000;
      let n = this.revealedN;
      while (n < this.wordStarts.length && this.wordStarts[n]! <= playedSec) n++;
      if (n !== this.revealedN) {
        this.revealedN = n;
        const words = this.revealText.trim().split(/\s+/).filter(Boolean);
        this.options.onReveal(words.slice(0, n).join(' '));
      }
      this.revealPoll = this.clock.setTimeout(tick, 60);
    };
    this.revealPoll = this.clock.setTimeout(tick, 60);
  }
  /** Stop the poll. `finalFlush` reveals the whole line once audio has finished, so a timing
   *  undershoot never leaves the tail unrevealed (skipped after a barge-in — the truncated prefix
   *  is what was actually spoken). */
  private stopWordReveal(finalFlush: boolean): void {
    if (this.revealPoll) { this.clock.clearTimeout(this.revealPoll); this.revealPoll = null; }
    if (finalFlush && this.options.revealMode === 'word' && !this.interrupted) {
      const full = this.revealText.trim();
      if (full) this.options.onReveal(full);
    }
  }
  /** close the spoken turn (idempotent); stays audible until ALL audio arrived AND playback drains */
  endSpeech(): void {
    this.interrupted = false; // the interrupted turn is over — next turn may speak
    this.clearAckTimer('turn_end'); // turn is over — a late ack would speak into the drain
    if (!this.speaking) return;
    this.diag('tts_turn_end', { spoke: this.spokeDeltas, replyChars: this.reply.length });
    this.ctxOpen = false; // context ends now; audio keeps draining
    // Flush any tail the emotion parser held back (a dangling `[…` partial, or a pending tag) so the
    // last words aren't swallowed; the real audio flush is tts.end()'s cont=false below.
    if (this.emo) { const t = this.emo.flush(); this.emo = null; if (t.prose) this.reply += t.prose; if (t.speech) this.tts.speak(forSpeech(t.speech), true); }
    if (this.reply) this.prevReply = this.reply;
    // remain 'speaking' (barge-in armed, echo discrimination active) until audio actually finishes
    const settle = () => {
      if (this.ctxOpen) { this.drainTimer = null; return; } // a newer turn took over the player — it settles
      if (this.pausedAt) { this.drainTimer = this.clock.setTimeout(settle, 250); return; } // held by an overlap — not finished audibly
      this.drainTimer = null;
      this.speaking = false;
      this.stopWordReveal(true); // audio finished — reveal any unrevealed tail
      if (this.turnStartAt) log.debug(`turn: ${Math.round(this.clock.now() - this.turnStartAt)}ms (incl. playback)`);
      this.echoUntil = this.clock.now() + 2500; // tail echo (capture+network+finalization) outlives playback
      if (!this.usingAec) this.stt.reset(); // drop echo residue (AEC: keep — it can only be the user)
      this.setState('listening');
      if (this.uttQueue.length) this.pumpQueue(); // next queued worker utterance plays after this one fully ends
    };
    const drainThenSettle = () => {
      if (this.drainTimer) this.clock.clearTimeout(this.drainTimer);
      this.drainTimer = this.clock.setTimeout(settle, this.player.drainMs() + 300);
    };
    if (this.spokeDeltas) {
      // the LLM turn is over but the TTS is still synthesizing — wait for ALL chunks (onDone)
      // before the drain countdown, else drainMs() undercounts and the reply's tail is dropped.
      this.tts.onDone = drainThenSettle;
      this.tts.end();
      // safety net: if 'done' never arrives (ws drop), settle after a generous ceiling
      if (this.drainTimer) this.clock.clearTimeout(this.drainTimer);
      this.drainTimer = this.clock.setTimeout(drainThenSettle, 15_000);
    } else drainThenSettle();
  }
  /** text of the reply cut by the last barge-in — consumed by the host to tell the model what
   *  the user did NOT hear. Cleared on read. */
  takeInterruptedReply(): { full: string; heard: string } | null {
    const r = this.lastInterrupted;
    this.lastInterrupted = null;
    return r;
  }

  /** Speak a short filler phrase without starting a model turn (stays in listening mode after). */
  speakFiller(text: string): void {
    if (!text || this.speaking) return;
    // A filler is NOT a reply: it must not flip repliedSinceDispatch, or a Hold's "mhm" would make
    // the re-finalization guard drop the fuller final the Hold is waiting for (starving the turn).
    const replied = this.repliedSinceDispatch;
    this.beginSpeech();
    this.speakDelta(text);
    this.endSpeech();
    this.repliedSinceDispatch = replied;
  }

  /** Enqueue a COMPLETE worker utterance (already-split spoken text) onto the central speech queue.
   *  If nothing is currently speaking it plays immediately; otherwise it queues and plays after the
   *  current utterance fully ends (settle → pumpQueue) — never spliced into an open reflex utterance. */
  enqueueUtterance(text: string): void {
    // Require speakable content (a letter/digit). A punctuation/quote-only utterance (e.g. a lone ")
    // produces no audio, so its own begin→end turn waits for a 'done' that never arrives and STALLS the
    // queue until the drain safety-net fires (~seconds of dead air). Drop it.
    if (!text || !/[\p{L}\p{N}]/u.test(text)) return;
    this.diag('enqueue_utterance', { chars: text.length, queued: this.uttQueue.length, speaking: this.speaking });
    this.uttQueue.push(text);
    if (!this.speaking) this.pumpQueue();
  }
  /** Play the next queued worker utterance as its own one-shot turn (begin → delta → end). Drives the
   *  next one from the settle completion (endSpeech), so utterances serialize without overlap. */
  private pumpQueue(): void {
    if (this.speaking) return; // a turn is in flight — settle will re-pump
    const text = this.uttQueue.shift();
    if (text == null) return;
    this.beginSpeech();
    this.speakDelta(text);
    this.endSpeech();
  }

  /** Short varied adaptive acks (adaptiveAckMs) — two shape pools picked by the dispatched
   *  utterance (question → thinking-ish, otherwise neutral/on-it), with anti-repetition (never one
   *  of the last 4 used). A 3-phrase round-robin sounded synthetic live ("started with 'hmm' too
   *  many times"). All phrases are sub-second and semantically safe for their shape.
   *  No 'Okay.': a genuine user "Okay." within the 6s squash window would be swallowed as ack echo. */
  static readonly ACKS_NEUTRAL = ['Mm-hm.', 'One sec.', 'Right.', 'Sec.', 'On it.', 'Sure, moment.', 'Uh, one moment.', 'Let me see.'];
  static readonly ACKS_QUESTION = ['Hmm.', 'Let me think.', 'Hm, let me see.', 'Good question.', 'Mm, one sec.', 'Let\'s see.'];
  private recentAcks: string[] = [];
  private pickAck(): string {
    const pool = this.lastDispatchWasQuestion ? VoiceEngine.ACKS_QUESTION : VoiceEngine.ACKS_NEUTRAL;
    const fresh = pool.filter((p) => !this.recentAcks.includes(p));
    const phrase = (fresh.length ? fresh : pool)[Math.floor(Math.random() * (fresh.length ? fresh.length : pool.length))];
    this.recentAcks.push(phrase);
    if (this.recentAcks.length > 4) this.recentAcks.shift();
    return phrase;
  }
  private lastDispatchWasQuestion = false;
  private clearAckTimer(reason?: string): void {
    if (!this.ackTimer) return;
    this.clock.clearTimeout(this.ackTimer);
    this.ackTimer = null;
    if (reason) this.diag('ack_cancelled', { reason });
  }
  /** Cancel a pending adaptive ack without touching the turn — hosts call this when the turn turns out
   *  to be a Hold (intentionally silent; an ack would read as the start of an answer). */
  cancelPendingAck(): void { this.clearAckTimer('hold'); }

  /** barge-in: stop audio NOW, cancel generation, reset for the user's utterance */
  interrupt(): void {
    this.clearAckTimer('interrupt'); // the turn the ack was masking is dead
    if (this.uttQueue.length) log.info(`barge-in dropped ${this.uttQueue.length} queued worker utterance(s)`); // forensics: a "lost" task delivery usually died here
    const droppedQueued = this.uttQueue.length;
    this.uttQueue = []; // barge-in drops any queued worker utterances (the user took the floor)
    if (!this.speaking && !this.drainTimer) return;
    this.stopWordReveal(false); // barge-in: freeze reveal at what was actually spoken (no tail flush)
    // Emitted for every EFFECTIVE interrupt — engine cede paths also emit a `barge_in` right after
    // (with the trigger); a lone `interrupt` with no adjacent barge_in = host-initiated (Esc, /voice).
    this.diag('interrupt', { droppedQueued, ctxOpen: this.ctxOpen, playedMs: Math.round(Math.max(0, this.player.playedMs())) });
    if (this.drainTimer) { this.clock.clearTimeout(this.drainTimer); this.drainTimer = null; }
    // estimate how much of the reply was actually heard: played-audio seconds ≈ spoken chars
    // (~15 chars/s of TTS speech) — coarse, but enough for "the user missed most of it".
    this.resetOverlap(false); // ceding — the flush below discards the held tape, don't resume it
    this.lastResumeAt = 0; // consume the re-pause signal: the next reply starts fresh
    const heardChars = Math.round((Math.max(0, this.player.playedMs()) / 1000) * 15); // paused time excluded by the sink
    if (this.reply) this.lastInterrupted = { full: this.reply, heard: this.reply.slice(0, heardChars) };
    this.speaking = false;
    this.ctxOpen = false;
    this.interrupted = true; // stragglers from the aborted stream stay silent
    this.suspectUntil = 0;
    // Meet's WebRTC echoes our audio back with ~1-2s network delay. The echo window must cover
    // the full playback duration + round-trip, not just a fixed tail. Without this, long responses
    // (stories, explanations) leak their echo tail past the window → false barge-in → cascade.
    this.echoUntil = this.clock.now() + Math.max(2500, this.player.drainMs() + 3000);
    this.tts.cancel();
    this.player.kill();
    if (!this.usingAec) this.stt.reset(); // heuristic: drop the mixed echo/user buffer
    if (this.reply) this.prevReply = this.reply;
    this.setState('listening');
  }
  stop(): void {
    this.uttQueue = [];
    this.clearAckTimer();
    if (this.resumeTimer) this.clock.clearTimeout(this.resumeTimer);
    if (this.pendingTimer) this.clock.clearTimeout(this.pendingTimer);
    if (this.drainTimer) this.clock.clearTimeout(this.drainTimer);
    if (this.specTimer) this.clock.clearTimeout(this.specTimer);
    this.bcSupersede();
    this.stt.stop();
    this.player.kill();
    this.tts.close();
    this.setState('idle');
  }

  // --- listening side (STT-driven) ---

  private words(s: string): string[] {
    return s.toLowerCase().replace(/[^a-z0-9\s]/g, '').split(/\s+/).filter((w) => w.length >= 2);
  }
  private novelWords(text: string): string[] {
    return this.words(text).filter((w) => !this.echoWords.has(w));
  }
  private echoActive(): boolean {
    return this.speaking || this.clock.now() < this.echoUntil;
  }
  /** Genuine user speech vs our own bleed (AEC tier): novel words must DOMINATE, not merely exist.
   *  Degraded AEC + an STT mis-hearing manufactures a single novel word out of pure echo (a name or
   *  rare word in our own reply comes back transcribed slightly differently — 1 novel / N words).
   *  A real interjection is mostly novel ("stop", "wait what") — short utterances pass on ratio,
   *  longer ones on count. */
  private genuine(text: string): boolean {
    const total = this.words(text).length;
    const novel = this.novelWords(text).length;
    // ratio, not count: a long mis-heard echo sentence yields 2-3 "novel" words out of a dozen
    // (live trace: "…latest email from you" from our "…latest news for you") — an absolute >=2
    // pass let those through. Real user speech is MOSTLY novel.
    return novel > 0 && novel / Math.max(1, total) > 0.5;
  }

  private handlePartial(text: string): void {
    if (this.speaking) {
      // Half-duplex (no AEC, no headphones): deaf while we speak — echo can't become a barge or partial.
      if (!this.options.bargeIn) return;
      // Barge grace: right after an utterance dispatched this turn, the user's OWN trailing audio is
      // still finalizing — it must not barge the reply they just requested (B3 over-interruption / B4
      // double-abort). Drop the barge/pause decision during the grace; still render the partial below.
      if (this.clock.now() < this.bargeGraceUntil) {
        if (this.lastGraceDiag !== this.bargeGraceUntil) { this.lastGraceDiag = this.bargeGraceUntil; this.diag('grace_suppress', { text: text.slice(0, 60) }); }
        if (!this.echoActive() || (this.usingAec ? this.genuine(text) : this.novelWords(text).length >= 1)) this.options.onPartial(text);
        return;
      }
      if (this.overlapCapable) {
        // STT-driven overlap: Soniox saying "this is speech" is the discriminator (energy could not
        // separate residue from speech across rooms). New partial text → pause (trail-off); growing
        // into dominant-novel ≥2 words → cede; stalling → the resume timer releases the hold.
        const txt = text.trim();
        if (!txt || txt === this.lastOverlapPartial) return; // no NEW speech activity
        this.lastOverlapPartial = txt;
        // Echo gate on the PAUSE, not just the cede: our own audio bleeds back through imperfect AEC and
        // Soniox transcribes it as evolving partials. Pausing on each one (the old behavior) was an
        // audible hiccup mid-reply for every echo fragment — the "TTS interrupting itself" bug. genuine()
        // (novel words must DOMINATE) is the SAME speech/echo discriminator already used to cede; apply it
        // to the pause so pure echo is inert. A real barge is novel by definition → still pauses instantly,
        // even on one word ("stop" → 1 novel / 1 total → genuine).
        if (!this.genuine(txt)) { if (this.pausedAt) this.armResume(); return; } // echo: inert (keep a live hold ticking)
        if (!this.pausedAt) {
          this.pausedAt = this.clock.now();
          const sinceResumeMs = this.lastResumeAt ? Math.round(this.clock.now() - this.lastResumeAt) : undefined;
          this.diag('overlap_pause', { text: txt.slice(0, 60), sinceResumeMs });
          this.player.pause!();
          // Re-pause right after a false-alarm resume → persistent human, not an echo blip. Cede now,
          // before the resume replays old audio again, and without waiting for the dominant-novel gate.
          if (this.lastResumeAt && this.clock.now() - this.lastResumeAt < this.options.overlapRepauseCedeMs) {
            const phase = this.ctxOpen ? 'speaking' as const : 'drain' as const;
            this.diag('barge_in', { phase, trigger: 'overlap_repause', sinceResumeMs });
            this.interrupt();
            this.options.onBargeIn(phase);
            return;
          }
        }
        if (this.words(txt).length >= 2) { // genuine already true → dominant-novel ≥2 words → cede
          const phase = this.ctxOpen ? 'speaking' as const : 'drain' as const;
          this.diag('barge_in', { phase, trigger: 'overlap_cede', novel: this.novelWords(txt).length, total: this.words(txt).length });
          this.interrupt();
          this.options.onBargeIn(phase);
          return;
        }
        this.armResume(); // genuine single word: hold; quiet releases
        return;
      }
      // Barge-in = NOVEL words (not in our own recent reply) — even with AEC. Cancellation quality
      // is a spectrum (VPIO convergence varies run to run); when it degrades, the assistant's own
      // bleed must not self-interrupt. User speech is novel by definition → still triggers fast.
      const barge = this.usingAec ? this.genuine(text) : this.novelWords(text).length >= (this.suspectUntil ? 1 : 2);
      if (barge) {
        const phase = this.ctxOpen ? 'speaking' as const : 'drain' as const;
        this.diag('barge_in', { phase, trigger: this.usingAec ? 'genuine' : 'heuristic_novel', novel: this.novelWords(text).length, total: this.words(text).length });
        this.interrupt();
        this.options.onBargeIn(phase);
      }
      return;
    }
    if (this.pendingUtt && text.trim()) { // user kept talking during the merge window — wait for the next endpoint
      // RE-ARM (long fallback), never just clear: Soniox emits trailing partial frames after <end> —
      // clearing left the pending utterance parked forever (the user's barge words were LOST).
      if (this.pendingTimer) this.clock.clearTimeout(this.pendingTimer);
      this.pendingTimer = this.clock.setTimeout(() => this.flushUtterance(), Math.max(800, this.options.utteranceMergeMs));
    }
    this.trackSpeculation(text);
    this.trackBackchannel(text);
    // display partials unless they're echo-shaped during/after our own speech (degraded-AEC bleed)
    if (!this.echoActive() || (this.usingAec ? this.genuine(text) : this.novelWords(text).length >= 1)) this.options.onPartial(text);
  }

  /** Speculative-reflex trigger (listening side only — the speaking branch returns before this): a
   *  partial that hasn't CHANGED for speculativeMs and has ≥ speculativeMinWords is stable → fire
   *  onSpeculate once. If the user then keeps talking well past the speculated text, onSpeculateAbort
   *  tells the host to kill the held call early. The confirm/abort DECISION belongs to the host at
   *  dispatch time (speculationConfirms against the real final) — flushUtterance only resets the
   *  trigger for the next turn. Merge windows are untouched: no speculation while an endpointed
   *  utterance is pending (the final would be the MERGED text, which the partial alone never matches). */
  private trackSpeculation(text: string): void {
    if (!this.options.speculativeMs) return;
    const t = text.trim();
    if (this.specText) {
      // in flight: partial grew ≥3 words past the speculated text → the final can no longer confirm
      if (this.words(t).length > this.words(this.specText).length + 2) {
        this.specText = '';
        this.diag('speculate_abort', { reason: 'partial_grew', partial: t.slice(0, 60) });
        this.options.onSpeculateAbort();
      }
      return;
    }
    if (this.specSpent || this.pendingUtt || !t || t === this.specPartial) return;
    this.specPartial = t;
    if (this.specTimer) this.clock.clearTimeout(this.specTimer);
    this.specTimer = this.clock.setTimeout(() => {
      this.specTimer = null;
      if (this.speaking || this.specSpent || this.pendingUtt) return;
      if (this.words(this.specPartial).length < this.options.speculativeMinWords) return;
      // Soniox emits trailing partial frames after <end> — a partial that just echoes the utterance we
      // JUST dispatched must not spawn a phantom speculation (its "final" already came and went).
      const flat = this.specPartial.toLowerCase().replace(/[^a-z0-9]/g, '');
      if (flat && this.lastDispatchFlat && this.clock.now() - this.lastDispatchAt < VoiceEngine.DUP_FINAL_MS &&
          (this.lastDispatchFlat.startsWith(flat) || flat.startsWith(this.lastDispatchFlat))) return;
      this.specSpent = true;
      this.specText = this.specPartial;
      log.debug(`speculate: "${this.specText.slice(0, 60)}"`);
      this.diag('speculate', { text: this.specText });
      this.options.onSpeculate(this.specText);
    }, this.options.speculativeMs);
  }

  /** Backchannel blip pool — short, quiet, semantically inert. Chosen to be transcript-safe: if
   *  imperfect AEC lets a blip reach Soniox MID-user-speech it lands inside their partial stream, so
   *  every phrase is a word whose accidental presence barely hurts a transcript, and flushUtterance
   *  strips an isolated echo of the exact phrase at a clause edge within 2s (stripBackchannelEcho).
   *  'Okay.'/'Right.' are fine here (unlike ACKS_NEUTRAL's no-Okay rule): a standalone user "Okay."
   *  right after OUR blip is overwhelmingly the blip's echo — the squash guard eating it is the point. */
  static readonly BACKCHANNELS = ['Mm-hm.', 'Uh-huh.', 'Right.', 'Okay.'];

  /** Backchannel trigger (listening side only — the speaking branch returns before this): a partial
   *  that reached a clause boundary and then stayed UNCHANGED for backchannelMs (a micro-pause, still
   *  BEFORE the silence endpoint) fires a blip — if long enough (≥ backchannelMinWords), predominantly
   *  Latin, rate-limited (backchannelMinGapMs + max 2/turn), and no endpointed text is pending.
   *  Touches nothing else: merge/endpoint/speculation timers and turn state are never affected. */
  private trackBackchannel(text: string): void {
    if (!this.options.backchannelMs) return;
    const t = text.trim();
    if (!t || t === this.bcPartial) return; // no NEW speech — the armed stability timer keeps ticking
    this.bcPartial = t;
    if (this.bcTimer) this.clock.clearTimeout(this.bcTimer);
    this.bcTimer = this.clock.setTimeout(() => {
      this.bcTimer = null;
      const p = this.bcPartial;
      const veto = (reason: string) => this.diag('backchannel_suppress', { reason });
      if (this.speaking || this.pendingUtt || this.bcActive) return veto('busy'); // never while audible or endpointed
      // Full-duplex (AEC) only: the blip extends echoUntil, and the half-duplex/heuristic echo gate
      // drops utterances wholesale while echo-active — a blip there would deafen the engine to the
      // user's own final for ~2.5s. Under AEC, genuine() keeps real speech flowing.
      if (!this.usingAec || !this.options.bargeIn) return veto('half_duplex');
      if (this.bcCount >= 2 || (this.lastBcAt && this.clock.now() - this.lastBcAt < this.options.backchannelMinGapMs)) return veto('rate_limited');
      if (this.words(p).length < this.options.backchannelMinWords) return veto('short');
      // clause boundary: trailing punctuation, or a mid-thought conjunction/filler pause
      if (!/[,.;:!?…]$/.test(p) && !this.looksIncomplete(p)) return veto('no_boundary');
      // Latin-predominant only (v1): the boundary/conjunction rules are English-tuned — Hebrew/mixed
      // partials get NO blips rather than mistimed ones (documented safe default).
      const letters = p.replace(/[^\p{L}]/gu, '');
      const latin = p.replace(/[^A-Za-z]/g, '');
      if (!letters || latin.length / letters.length < 0.7) return veto('non_latin');
      // trailing partial frames of the utterance we JUST dispatched must not blip at its ghost
      const flat = p.toLowerCase().replace(/[^a-z0-9]/g, '');
      if (flat && this.lastDispatchFlat && this.clock.now() - this.lastDispatchAt < VoiceEngine.DUP_FINAL_MS &&
          (this.lastDispatchFlat.startsWith(flat) || flat.startsWith(this.lastDispatchFlat))) return;
      this.fireBackchannel();
    }, this.options.backchannelMs);
  }

  /** Speak one blip on a THROWAWAY TTS context. Zero floor-claim: no `speaking`, no state change, no
   *  markTurn, no merge/endpoint/barge timers, no repliedSinceDispatch, no utterance queue. Echo
   *  pre-seeding happens BEFORE any audio exists: the blip's words join echoWords (its mic echo is
   *  never "novel"), the ack-squash guard is armed with the exact phrase (a standalone echo final is
   *  swallowed), and the echo window extends so echo-shaped finals stay gated. */
  private fireBackchannel(): void {
    const fresh = VoiceEngine.BACKCHANNELS.filter((x) => !this.recentBc.includes(x));
    const pool = fresh.length ? fresh : VoiceEngine.BACKCHANNELS;
    const phrase = pool[Math.floor(Math.random() * pool.length)];
    this.recentBc.push(phrase);
    if (this.recentBc.length > 2) this.recentBc.shift();
    const now = this.clock.now();
    for (const w of this.words(phrase)) this.echoWords.add(w);
    this.ackAt = now; this.lastAck = phrase; // squash guard: a standalone echo of the blip is dropped
    this.lastBcAt = now; this.lastBcPhrase = phrase;
    this.echoUntil = Math.max(this.echoUntil, now + 2500);
    this.bcCount++;
    this.bcActive = true; // open the narrow audio-gate bypass for THIS blip only
    if (this.bcActiveTimer) this.clock.clearTimeout(this.bcActiveTimer);
    this.bcActiveTimer = this.clock.setTimeout(() => { this.bcActive = false; }, 3000);
    this.tts.onDone = () => { this.bcActive = false; }; // safe: reassigned by the next endSpeech
    this.tts.newContext(); // throwaway context — a real turn's newContext() supersedes it (stale filter)
    this.tts.speak(phrase, false);
    log.debug(`backchannel: "${phrase}"`);
    this.diag('backchannel', { phrase });
    this.options.onBackchannel(phrase);
  }

  /** A real turn (or shutdown) takes over mid-blip: close the audio bypass + stability timer. */
  private bcSupersede(): void {
    this.bcActive = false;
    if (this.bcActiveTimer) { this.clock.clearTimeout(this.bcActiveTimer); this.bcActiveTimer = null; }
    if (this.bcTimer) { this.clock.clearTimeout(this.bcTimer); this.bcTimer = null; }
  }

  /** Strip the mic echo of the LAST blip from a dispatching final, conservatively: only the exact
   *  phrase, as an isolated token at a clause edge (start/end of utterance or beside punctuation),
   *  within 2s of the blip. "Right"/"okay" as genuine mid-sentence content words are never touched. */
  private stripBackchannelEcho(text: string): string {
    if (!this.lastBcPhrase || this.clock.now() - this.lastBcAt > 2000) return text;
    const target = normWord(this.lastBcPhrase);
    if (!target) return text;
    const toks = text.split(/\s+/);
    const idx = toks.findIndex((tok, i) => {
      if (normWord(tok) !== target) return false;
      if (/\?/.test(tok)) return false; // a tag question ("…tomorrow, right?") is user MEANING, not echo — a blip's echo never gains a '?'
      const edgeBefore = i === 0 || /[,.;:!?…]$/.test(toks[i - 1]);
      const edgeAfter = i === toks.length - 1 || /[,.;:!?…]$/.test(tok);
      return edgeBefore && edgeAfter;
    });
    if (idx < 0) return text;
    toks.splice(idx, 1);
    log.verbose(`stripped backchannel echo "${this.lastBcPhrase}" from dispatching final`);
    return toks.join(' ');
  }

  /** Merge a resumed utterance into the pending one, deduping any word-overlap. Soniox re-finalizes
   *  overlapping audio when the silence-timer and the semantic `<end>` both endpoint a growing
   *  utterance (or after a reconnect): the next "utterance" repeats the tail of the previous one, and
   *  a naive `${prev} ${next}` produced the live duplication ("Um, I want to check if Um, I want to
   *  check if…"). Find the longest suffix of `prev`'s words that prefixes `next` and drop it. */
  private mergeUtterance(prev: string, next: string): string {
    if (!prev) return next;
    if (!next) return prev;
    const pw = prev.split(/\s+/), nw = next.split(/\s+/);
    const norm = VoiceEngine.normWord;
    // Truncation repair: the previous final is a strict PREFIX of this one (Soniox re-finalized the
    // same audio with a longer tail — live: "Hey ther" then "Hey there, how are you?"). Keep the
    // longer final instead of concatenating a truncated ghost in front of it. Conservative: prev must
    // have ≥2 words (a genuine one-word utterance followed by more speech is normal continuation),
    // every earlier word must match, and prev's LAST word may be a char-prefix of next's ("ther"→"there").
    if (pw.length >= 2 && pw.length <= nw.length) {
      let pfx = true;
      for (let i = 0; i < pw.length - 1; i++) if (norm(pw[i]) !== norm(nw[i])) { pfx = false; break; }
      if (pfx && norm(nw[pw.length - 1]).startsWith(norm(pw[pw.length - 1]))) return next;
    }
    const max = Math.min(pw.length, nw.length);
    for (let k = max; k > 0; k--) {
      let match = true;
      for (let i = 0; i < k; i++) if (norm(pw[pw.length - k + i]) !== norm(nw[i])) { match = false; break; }
      if (match) return [...pw, ...nw.slice(k)].join(' ');
    }
    return `${prev} ${next}`;
  }

  private static normWord(w: string): string { return normWord(w); }

  /** Soniox re-finalization of an ALREADY-DISPATCHED utterance, arriving PAST the merge window:
   *  the new final is a strict word-prefix superset of the last dispatch (the last dispatched word
   *  may be a char-prefix of the corresponding new word — a mid-word endpoint), and it lands within
   *  DUP_FINAL_MS of the dispatch. Live: "Hi, please tell me a very short" dispatched, then the full
   *  "…very short joke." re-finalized ~900ms later → dispatched TWICE (two replies, the second to a
   *  question already being answered).
   *  DESIGN — HYBRID at the caller (flushUtterance): this shape check identifies a re-finalization
   *  (a human physically cannot re-speak a ≥3-word sentence plus extra words within 3s of the
   *  previous dispatch; a genuine continuation arrives as NEW words, never as a superset), and
   *  repliedSinceDispatch then picks the action:
   *  • reply already streaming (the live trace: TTFT ~500ms < the ~900ms re-final) → DROP. True
   *    "supersede" would mean aborting audible speech mid-word to re-answer nearly the same text —
   *    worse UX — and needs turn-abort plumbing in every host (the lab bridge has none;
   *    DuplexAgent.send is a non-cancelable queue, so a second send just stacks a SECOND full reply).
   *    Soniox's premature endpoint fires at a prosodic boundary, so the loss is trailing word(s).
   *  • NO reply yet → DISPATCH the fuller text. Live-verified necessity: the reflex Holds on the
   *    truncated fragment ("Hi, please tell me a very short" → Hold), and dropping the re-final then
   *    starves the conversation entirely — the fuller final IS the completion the Hold is waiting
   *    for. A slow-but-answering reflex in this window degrades to today's two-reply behavior (rare
   *    race), never worse. Fillers ("mhm") deliberately don't count as replies — see speakFiller. */
  private refinalizes(next: string): boolean {
    const pw = this.lastDispatchWords, nw = next.trim().split(/\s+/);
    const norm = VoiceEngine.normWord;
    if (pw.length < 3 || nw.length < pw.length) return false;
    if (this.clock.now() - this.lastDispatchAt >= VoiceEngine.DUP_FINAL_MS) return false;
    for (let i = 0; i < pw.length - 1; i++) if (norm(pw[i]) !== norm(nw[i])) return false;
    const pLast = norm(pw[pw.length - 1]), nSame = norm(nw[pw.length - 1]);
    if (!nSame.startsWith(pLast)) return false;
    return nw.length > pw.length || nSame.length > pLast.length; // STRICT superset (identical → dup guard, which allows post-reply repeats)
  }

  private static readonly TRAIL_RE = /(?:^|\s)(?:and|but|or|so|to|the|a|an|of|in|for|with|that|if|uh|um|like|about|from|into|on|is|are|was|were|,)$/i;
  /** The utterance sounds like the user paused mid-thought (trailing conjunction/filler/comma). */
  private looksIncomplete(text: string): boolean {
    return VoiceEngine.TRAIL_RE.test(text.trim());
  }

  private handleUtterance(text: string): void {
    // Overlap that never grabbed the turn: duration said backchannel/noise → drop it. Real grabs
    // interrupted already (speaking=false). Applies while content flows (ctxOpen) AND while we are
    // holding a pause for THIS overlap (a drain-tail "yeah" leaked as a real turn otherwise). A
    // clean early answer during the drain — no hold in progress — still falls through (turn-taking).
    if (this.speaking && (this.ctxOpen || this.pausedAt) && this.overlapCapable) { this.diag('utterance_drop', { reason: 'overlap_noise', text: text.slice(0, 60) }); this.stt.reset(); return; }
    // Echo-shaped utterances (no/few novel words while we are or just were audible) are dropped in
    // BOTH tiers — degraded AEC bleed otherwise becomes fake user turns ("🎤 › Friday.").
    // Half-duplex: drop EVERY utterance while audible (speaking + drain tail) — pure echo suppression.
    if (this.echoActive() && (!this.options.bargeIn || (this.usingAec ? !this.genuine(text) : this.novelWords(text).length < 2))) { this.diag('echo_swallow', { guard: 'echo_gate', text: text.slice(0, 60) }); this.stt.reset(); return; }
    // AEC converges over ~100ms at audio onset — the ack (first audio after silence) can leak back
    // as a transcript. Drop backchannel-shaped utterances shortly after we spoke it.
    const squash = (t: string) => t.toLowerCase().replace(/[^a-z]/g, '').replace(/(.)\1+/g, '$1');
    if (this.ackAt && this.lastAck && this.clock.now() - this.ackAt < 6000 && squash(text) === squash(this.lastAck)) { this.diag('echo_swallow', { guard: 'ack_squash', text: text.slice(0, 60) }); this.ackAt = 0; return; }
    // Merge window: semantic endpointing fires mid-spelling ("E-L-Y." … "A.") and mid-thought.
    // Hold briefly; speech resuming in the window appends to the SAME utterance. Substantial
    // utterances (≥4 words) skip the hold — they are never spelled fragments, and the saved 350ms
    // is the difference between ping-pong and lag in quick exchanges.
    if (this.pendingUtt) this.mergePath = 'merged'; // this final joined a held one (see dispatch diag)
    this.pendingUtt = this.mergeUtterance(this.pendingUtt, text);
    if (this.pendingTimer) this.clock.clearTimeout(this.pendingTimer);
    // Incomplete utterance hold: trailing conjunctions/fillers get an extended merge window + optional
    // filler phrase, letting the user finish their thought without triggering a model call.
    if (this.options.incompleteMergeMs && this.looksIncomplete(this.pendingUtt)) {
      log.verbose(`hold: incomplete utterance "${this.pendingUtt.slice(-40)}"`);
      this.diag('hold', { reason: 'incomplete', tail: this.pendingUtt.slice(-40) });
      this.options.onHold();
      if (this.options.holdFiller && !this.speaking) this.speakFiller(this.options.holdFiller); // filler ≠ reply (see speakFiller)
      this.pendingTimer = this.clock.setTimeout(() => this.flushUtterance(), this.options.incompleteMergeMs);
      return;
    }
    if (!this.options.utteranceMergeMs || this.words(this.pendingUtt).length >= 4) return this.flushUtterance();
    this.pendingTimer = this.clock.setTimeout(() => this.flushUtterance(), this.options.utteranceMergeMs);
  }
  private flushUtterance(): void {
    if (this.pendingTimer) { this.clock.clearTimeout(this.pendingTimer); this.pendingTimer = null; }
    // The turn-in-progress is dispatching (or dropping) — reset the speculation trigger for the next
    // one. The host decides confirm/abort of any in-flight speculative call against the final itself.
    if (this.specTimer) { this.clock.clearTimeout(this.specTimer); this.specTimer = null; }
    this.specSpent = false; this.specPartial = ''; this.specText = '';
    // Backchannel per-turn reset: the turn-in-progress ends here. A pending stability timer must not
    // fire a blip over the dispatch, and the next user turn gets a fresh 2-blip budget.
    if (this.bcTimer) { this.clock.clearTimeout(this.bcTimer); this.bcTimer = null; }
    this.bcCount = 0; this.bcPartial = '';
    const text = this.stripBackchannelEcho(this.pendingUtt); // drop the blip's mic echo (isolated, clause-edge, <2s)
    this.pendingUtt = '';
    const path = this.mergePath; this.mergePath = 'direct';
    if (text) {
      const flat = text.toLowerCase().replace(/[^a-z0-9]/g, '');
      if (flat && flat === this.lastDispatchFlat && !this.repliedSinceDispatch &&
          this.clock.now() - this.lastDispatchAt < VoiceEngine.DUP_FINAL_MS) {
        log.verbose(`dropped duplicate final "${text.slice(0, 40)}" (identical to the just-dispatched utterance, no reply between)`);
        this.diag('utterance_drop', { reason: 'dup_final', text: text.slice(0, 60) });
        return;
      }
      const refinal = this.refinalizes(text);
      if (refinal && this.repliedSinceDispatch) {
        // Track the FULLEST text so a third, even-fuller re-final still matches; lastDispatchAt stays
        // anchored at the real dispatch (re-finals must not extend their own window).
        log.verbose(`dropped re-finalization "${text.slice(0, 60)}" (superset of the just-dispatched utterance, reply already flowing)`);
        this.diag('utterance_drop', { reason: 'refinalized', text: text.slice(0, 60) });
        this.lastDispatchFlat = flat;
        this.lastDispatchWords = text.trim().split(/\s+/);
        return;
      }
      this.lastDispatchFlat = flat;
      this.lastDispatchWords = text.trim().split(/\s+/);
      this.lastDispatchAt = this.clock.now();
      this.repliedSinceDispatch = false;
      this.lastDispatchWasQuestion = /\?\s*$/.test(text); // shapes the adaptive-ack pool (question → thinking-ish)
      this.turnStartAt = this.clock.now();
      // Trailing audio of the utterance we just dispatched keeps finalizing for a beat (Soniox emits
      // partials after <end>). Once the host opens a reply context (speaking=true), those self-tail
      // partials would read as a barge and abort the very reply the user asked for (the live "interrupts
      // mid-sentence" + double-abort steps=1→steps=0). Grace the barge path briefly after dispatch.
      this.bargeGraceUntil = this.clock.now() + this.options.bargeGraceMs;
      this.diag('dispatch', { text, path: refinal ? 'refinalized' : path, question: this.lastDispatchWasQuestion });
      this.options.onUtterance(text); // stamp the real turn start for TTFT
    }
  }

  private get overlapCapable(): boolean {
    return this.usingAec && this.options.overlapPause && !!this.player.pause && !!this.player.resume;
  }
  private armResume(): void {
    if (this.resumeTimer) this.clock.clearTimeout(this.resumeTimer);
    this.resumeTimer = this.clock.setTimeout(() => {
      this.resumeTimer = null;
      if (!this.pausedAt) return; // (no speaking guard: a held pause must ALWAYS release, else the tape wedges)
      this.diag('overlap_resume', { heldMs: Math.round(this.clock.now() - this.pausedAt) });
      this.stt.reset(); // drop the stalled residue/backchannel buffer — it is not the next utterance
      this.resetOverlap(true); // the overlap died out (backchannel/noise) — pick up exactly where she left off
    }, this.options.overlapResumeMs);
  }
  private resetOverlap(resume: boolean): void {
    if (this.resumeTimer) { this.clock.clearTimeout(this.resumeTimer); this.resumeTimer = null; }
    if (this.pausedAt && resume) { this.player.resume?.(); this.lastResumeAt = this.clock.now(); }
    this.pausedAt = 0;
    this.lastOverlapPartial = '';
    this.gatePassTimes = [];
  }

  /** energy two-stage barge-in (heuristic tier only): spike over echo baseline → pause + confirm via STT */
  private gatePassTimes: number[] = []; // recent gate-PASSING chunks (helper zeroes residue — nonzero = vetted)
  private handleLevel(rms: number): void {
    if (this.usingAec) {
      // Fast hold trigger: a nonzero level while we are audible means the helper's gate PASSED it
      // (≥2× expected residue). Two passes within 350ms = speech onset → pause NOW (~300ms), without
      // waiting for STT tokens (which lag whenever the onset straddles the gate). The STT still owns
      // the DECISION: cede on genuine ≥2 words, or the resume timer releases the hold.
      if (!this.options.overlapEnergyHold || !this.speaking || !this.overlapCapable || this.pausedAt || rms < 50) return;
      const t = this.clock.now();
      this.gatePassTimes = this.gatePassTimes.filter((x) => t - x < 350);
      this.gatePassTimes.push(t);
      if (this.gatePassTimes.length < 2) return;
      this.gatePassTimes = [];
      this.pausedAt = t;
      this.player.pause!();
      this.armResume();
      return;
    }
    if (!this.speaking) { this.baseline = 0; this.hot = 0; return; }
    // warm-up: let the EMA learn the echo level before triggers (else her own onset reads as a spike)
    if (!this.baseline) { this.baseline = rms; return; }
    this.baseline = this.baseline * 0.9 + rms * 0.1;
    if (rms > Math.max(this.baseline * this.options.bargeRmsMult, this.options.bargeRmsFloor)) this.hot++;
    else this.hot = 0;
    if (this.hot >= 2 && !this.suspectUntil) {
      this.suspectUntil = this.clock.now() + 1300;
      this.clock.setTimeout(() => { this.suspectUntil = 0; }, 1350);
    }
  }
}
